import React from 'react';
import { FlexDiv, TextDefault } from './ui';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    ensureYesNoListActions,
    getYesNoScore,
    removeYesNoSubAction,
    renameNestedChildScoringAction,
    setYesNoScore,
    setYesNoSharedComponent,
    getYesNoSharedScoringComponent,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';

const paperSx = {
    p: 2,
    mb: 2,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'grey.50',
    boxShadow: 1,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
};

function YesNoItemIdField({
    blockId,
    questionIndex,
    value,
    parentActionId,
    questionRow,
    questionsSnapshot,
    onPatchQuestion,
    onUpdateBlock,
}) {
    const [draft, setDraft] = React.useState(value || '');
    React.useEffect(() => {
        setDraft(value || '');
    }, [value]);

    return (
        <TextField
            size="small"
            label="Item ID"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
                const n = draft.trim();
                const o = String(value || '').trim();
                if (n === o) return;
                if (!n) {
                    setDraft(o);
                    return;
                }
                const dup = (questionsSnapshot || []).some(
                    (qq, i) =>
                        i !== questionIndex &&
                        String(qq?.actionId || '').trim() === n
                );
                if (dup) {
                    setDraft(o);
                    return;
                }
                if (!onPatchQuestion || !parentActionId) {
                    const next = [...questionsSnapshot];
                    next[questionIndex] = {
                        ...questionRow,
                        actionId: n,
                        type: questionRow.type || 'yes-no-question',
                    };
                    onUpdateBlock({ questions: next });
                    return;
                }
                onPatchQuestion((prev) => {
                    const block = prev.ui.blocks.find((b) => b.id === blockId);
                    if (!block) return prev;
                    const qs = [...(block.questions || [])];
                    if (
                        qs.some(
                            (qq, i) =>
                                i !== questionIndex &&
                                String(qq?.actionId || '').trim() === n
                        )
                    ) {
                        return prev;
                    }
                    const pid = String(block.actionId || '').trim();
                    let actions = { ...(prev.actions || {}) };
                    if (pid && o && actions[pid]?.[o] != null) {
                        const renamed = renameNestedChildScoringAction(
                            actions,
                            pid,
                            o,
                            n
                        );
                        if (renamed == null) {
                            return prev;
                        }
                        actions = renamed;
                    }
                    qs[questionIndex] = {
                        ...qs[questionIndex],
                        actionId: n,
                        type: qs[questionIndex].type || 'yes-no-question',
                    };
                    const blocks = prev.ui.blocks.map((b) =>
                        b.id === blockId ? { ...b, questions: qs } : b
                    );
                    return {
                        ...prev,
                        actions,
                        ui: { ...prev.ui, blocks },
                    };
                });
            }}
            placeholder="e.g. admin_password"
        />
    );
}

export function YesNoListBlockEditor({
    selectedBlock,
    question,
    scoringComponentOptions,
    onPatchQuestion,
    onUpdateBlock,
}) {
    if (selectedBlock.type !== 'yes-no-list') return null;

    const questions = selectedBlock.questions || [];
    const aid = selectedBlock.actionId;
    const minQuestions = 1;

    return (
        <FlexDiv
            column
            gap={1}
            $noWrap
            style={{ minWidth: 0, width: '100%' }}
        >
            <TextDefault bold>Yes / No questions and scores</TextDefault>
            <TextDefault lighter>
                Each card is one line in the list. Set the scores for Yes and No,
                and pick how the answer counts toward learning goals.
            </TextDefault>
            {questions.map((q, index) => {
                const subId = q.actionId;
                const canRemove = questions.length > minQuestions;
                return (
                    <Paper
                        key={`${selectedBlock.id}-yn-${index}`}
                        variant="outlined"
                        sx={paperSx}
                    >
                        <Stack spacing={2} sx={{ minWidth: 0, width: '100%' }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <TextDefault bold>
                                    Question {index + 1}
                                </TextDefault>
                                <IconButton
                                    size="small"
                                    disabled={!canRemove || !onPatchQuestion}
                                    aria-label="Remove question"
                                    onClick={() => {
                                        if (!onPatchQuestion) return;
                                        onPatchQuestion((prev) => {
                                            const bid = selectedBlock.id;
                                            const block = prev.ui.blocks.find(
                                                (b) => b.id === bid
                                            );
                                            if (!block) return prev;
                                            const sid = block.questions[index]
                                                ?.actionId;
                                            const newQuestions =
                                                block.questions.filter(
                                                    (_, i) => i !== index
                                                );
                                            let actions = removeYesNoSubAction(
                                                prev.actions || {},
                                                block.actionId,
                                                sid
                                            );
                                            const blocks = prev.ui.blocks.map(
                                                (b) =>
                                                    b.id === bid
                                                        ? {
                                                              ...b,
                                                              questions:
                                                                  newQuestions,
                                                          }
                                                        : b
                                            );
                                            return {
                                                ...prev,
                                                ui: { ...prev.ui, blocks },
                                                actions,
                                            };
                                        });
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                            <TextField
                                size="small"
                                label="Question text"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={q.text || ''}
                                onChange={(e) => {
                                    const newQuestions = [...questions];
                                    newQuestions[index] = {
                                        ...q,
                                        text: e.target.value,
                                    };
                                    onUpdateBlock({ questions: newQuestions });
                                }}
                            />
                            <YesNoItemIdField
                                blockId={selectedBlock.id}
                                questionIndex={index}
                                value={subId}
                                parentActionId={aid}
                                questionRow={q}
                                questionsSnapshot={questions}
                                onPatchQuestion={onPatchQuestion}
                                onUpdateBlock={onUpdateBlock}
                            />
                            {subId && aid && onPatchQuestion ? (
                                <Stack spacing={2}>
                                    <Stack
                                        direction="column"
                                        spacing={2}
                                        alignItems="stretch"
                                        sx={{ width: '100%', minWidth: 0 }}
                                    >
                                        <TextField
                                            label="Yes score"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ step: 'any' }}
                                            value={getYesNoScore(
                                                question.actions,
                                                aid,
                                                subId,
                                                'yes'
                                            )}
                                            onChange={(e) => {
                                                onPatchQuestion((prev) => ({
                                                    ...prev,
                                                    actions: setYesNoScore(
                                                        prev.actions || {},
                                                        aid,
                                                        subId,
                                                        'yes',
                                                        e.target.value,
                                                        prev
                                                    ),
                                                }));
                                            }}
                                        />
                                        <TextField
                                            label="No score"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ step: 'any' }}
                                            value={getYesNoScore(
                                                question.actions,
                                                aid,
                                                subId,
                                                'no'
                                            )}
                                            onChange={(e) => {
                                                onPatchQuestion((prev) => ({
                                                    ...prev,
                                                    actions: setYesNoScore(
                                                        prev.actions || {},
                                                        aid,
                                                        subId,
                                                        'no',
                                                        e.target.value,
                                                        prev
                                                    ),
                                                }));
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                width: '100%',
                                                minWidth: 0,
                                            }}
                                        >
                                            <ScoringComponentSelect
                                                componentOptions={
                                                    scoringComponentOptions
                                                }
                                                label="Learning goal"
                                                value={getYesNoSharedScoringComponent(
                                                    question.actions,
                                                    aid,
                                                    subId,
                                                    question
                                                )}
                                                onChange={(comp) => {
                                                    onPatchQuestion((prev) => ({
                                                        ...prev,
                                                        actions:
                                                            setYesNoSharedComponent(
                                                                prev.actions ||
                                                                    {},
                                                                aid,
                                                                subId,
                                                                comp,
                                                                prev
                                                            ),
                                                    }));
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                </Stack>
                            ) : (
                                <TextDefault lighter>
                                    Add an Item ID above to set scores for this
                                    line.
                                </TextDefault>
                            )}
                        </Stack>
                    </Paper>
                );
            })}
            {onPatchQuestion && aid ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label="Add question"
                        onClick={() => {
                            onPatchQuestion((prev) => {
                                const bid = selectedBlock.id;
                                const block = prev.ui.blocks.find(
                                    (b) => b.id === bid
                                );
                                if (!block?.actionId) return prev;
                                const stamp = Date.now();
                                const newSubId = `yn_${stamp}_${Math.random().toString(36).slice(2, 6)}`;
                                const newQ = {
                                    text: '',
                                    type: 'yes-no-question',
                                    actionId: newSubId,
                                };
                                const newQuestions = [
                                    ...(block.questions || []),
                                    newQ,
                                ];
                                const nextActions = ensureYesNoListActions(
                                    prev.actions || {},
                                    { ...block, questions: newQuestions },
                                    prev
                                );
                                const blocks = prev.ui.blocks.map((b) =>
                                    b.id === bid
                                        ? { ...b, questions: newQuestions }
                                        : b
                                );
                                return {
                                    ...prev,
                                    ui: { ...prev.ui, blocks },
                                    actions: nextActions,
                                };
                            });
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                    <TextDefault lighter>Add question</TextDefault>
                </Stack>
            ) : null}
        </FlexDiv>
    );
}
